import { useState } from "react";
import { useNavigate } from "react-router";
import { limit, orderBy } from "firebase/firestore";
import { AlertTriangle, Bot, CalendarPlus, MessageCircle, Globe } from "lucide-react";
import { formatClinicDate, formatClinicTime, type AssistantRequest } from "@cop/domain";
import { api } from "../lib/api";
import { useCatalog, useLive } from "../lib/data";
import { errorMessage } from "../lib/firebase";

const INTENT: Record<string, string> = { book: "Pide turno", reschedule: "Reprogramar", cancel: "Cancelar", question: "Consulta", urgent: "Urgencia", other: "Otro" };
const STATUS: Record<string, string> = { new: "Nueva", proposed: "Con horarios sugeridos", needs_human: "Requiere atención", booked: "Agendada", dismissed: "Descartada" };
const TIME: Record<string, string> = { morning: "por la mañana", afternoon: "por la tarde", any: "" };
const DAYS = ["", "lun", "mar", "mié", "jue", "vie", "sáb", "dom"];

type Row = AssistantRequest & { suggestedStarts?: string[] };

export function Requests() {
  const nav = useNavigate();
  const { services } = useCatalog();
  const [filter, setFilter] = useState<"open" | "all">("open");
  const [error, setError] = useState<string | null>(null);
  const { data } = useLive<Row>("requests", [orderBy("createdAt", "desc"), limit(100)]);
  const rows = (data ?? []).filter((r) => filter === "all" || ["new", "proposed", "needs_human"].includes(r.status));
  const urgentFirst = [...rows].sort((a, b) => Number(b.interpretation?.intent === "urgent") - Number(a.interpretation?.intent === "urgent"));

  async function dismiss(id: string) {
    try {
      await api.resolveRequest({ requestId: id, status: "dismissed" });
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  return (
    <div className="page">
      <header className="toolbar">
        <div className="toolbar-title">
          <h1>Solicitudes</h1>
          <p className="muted small">Pedidos escritos en la web o por WhatsApp, interpretados por el asistente. Vos decidís qué se agenda.</p>
        </div>
        <div className="segmented" role="tablist">
          <button role="tab" aria-selected={filter === "open"} onClick={() => setFilter("open")}>
            Abiertas
          </button>
          <button role="tab" aria-selected={filter === "all"} onClick={() => setFilter("all")}>
            Todas
          </button>
        </div>
      </header>
      {error && <p className="notice notice-danger">{error}</p>}

      {data === null ? (
        <div className="skeleton-rows" aria-busy="true" />
      ) : urgentFirst.length === 0 ? (
        <div className="empty">
          <Bot size={28} strokeWidth={1.25} aria-hidden />
          <h2>No hay solicitudes abiertas</h2>
          <p>Cuando alguien escriba en el asistente de la web o por WhatsApp (vía n8n), su pedido aparece acá con el tratamiento y los horarios sugeridos.</p>
        </div>
      ) : (
        <ul className="request-list">
          {urgentFirst.map((r) => {
            const i = r.interpretation;
            const service = services.find((s) => s.id === i?.serviceId);
            const urgent = i?.intent === "urgent";
            const prefs = [i?.preferredDays.map((d) => DAYS[d]).join(", "), TIME[i?.timeOfDay ?? "any"]].filter(Boolean).join(" ");
            return (
              <li key={r.id} className="request" data-urgent={urgent || undefined}>
                <div className="request-meta">
                  <span className="row gap-4 center small muted">
                    {r.source === "whatsapp" ? <MessageCircle size={14} aria-hidden /> : <Globe size={14} aria-hidden />}
                    {r.source === "whatsapp" ? "WhatsApp" : "Web"} · {new Date(r.createdAt).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" })}
                  </span>
                  <span className="tag" data-kind={urgent ? "danger" : r.status === "needs_human" ? "warn" : "info"}>
                    {urgent && <AlertTriangle size={12} aria-hidden />} {STATUS[r.status]}
                  </span>
                </div>
                <blockquote className="request-text">{r.rawText}</blockquote>
                {i && (
                  <p className="request-reading">
                    <Bot size={14} aria-hidden /> <strong>{INTENT[i.intent]}</strong>
                    {service && <> · {service.name}</>}
                    {prefs && <> · {prefs}</>}
                    {i.summary && !i.summary.startsWith("Interpretación") && <span className="muted"> — {i.summary}</span>}
                    <span className="muted small"> ({r.model === "heuristic" || r.model === "heuristic-fallback" ? "sin IA" : `${Math.round(i.confidence * 100)}%`})</span>
                  </p>
                )}
                {(r.contact?.name || r.contact?.phone) && (
                  <p className="small">
                    {r.contact.name} {r.contact.phone && <a href={`https://wa.me/${r.contact.phone.replace(/\D/g, "")}`}>{r.contact.phone}</a>}
                  </p>
                )}
                {r.suggestedStarts && r.suggestedStarts.length > 0 && (
                  <p className="small muted">
                    Sugeridos: {r.suggestedStarts.slice(0, 4).map((s) => `${formatClinicDate(s, { weekday: "short", month: undefined })} ${formatClinicTime(s)}`).join(" · ")}
                  </p>
                )}
                {["new", "proposed", "needs_human"].includes(r.status) && (
                  <div className="row gap-8 wrap">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() =>
                        nav("/agenda", {
                          state: { prefill: { serviceId: i?.serviceId ?? undefined, contactName: r.contact?.name, contactPhone: r.contact?.phone, notes: i?.summary, requestId: r.id } },
                        })
                      }
                    >
                      <CalendarPlus size={14} aria-hidden /> Agendar
                    </button>
                    <button className="btn btn-sm" onClick={() => dismiss(r.id)}>
                      Descartar
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
